<template>
  <div class="order-list-container">
    <div class="main-content">
      <el-card shadow="never" class="main-card">
        <div slot="header" class="card-header">
          <div class="header-title">
            <i class="el-icon-document-copy header-icon" />
            <span>订单中心</span>
          </div>
          <el-button type="text" icon="el-icon-refresh" class="refresh-btn" @click="fetchOrderList">
            刷新列表
          </el-button>
        </div>

        <!-- 1. 状态筛选 Tabs -->
        <el-tabs v-model="activeTab" class="custom-tabs" @tab-click="handleTabClick">
          <el-tab-pane label="全部订单" name="ALL" />
          <el-tab-pane label="待支付" name="UNPAID" />
          <el-tab-pane label="已支付" name="PAID" />
          <el-tab-pane label="已取消" name="CANCELLED" />
        </el-tabs>

        <!-- 2. 订单列表 -->
        <div v-loading="loading" class="order-list-body">
          <div v-if="orderList.length === 0" class="empty-box">
            <el-empty description="暂无符合条件的订单" :image-size="120" />
          </div>

          <div v-for="order in orderList" :key="order.orderId" class="order-item-card">
            <!-- 订单头部栏 -->
            <div class="order-item-header">
              <div class="header-left">
                <span class="order-time"><i class="el-icon-time" /> {{ order.createTime }}</span>
                <span class="order-id">订单号：<strong>{{ order.orderId }}</strong></span>
              </div>
              <div class="header-right">
                <el-tag :type="getStatusTagType(order.status)" size="small" effect="light" class="status-tag">
                  <i class="status-dot" :class="'dot-' + getStatusTagType(order.status)" />
                  {{ getStatusText(order.status) }}
                </el-tag>
              </div>
            </div>

            <!-- 订单内容区域 -->
            <div class="order-item-body">
              <div class="goods-info">
                <img :src="order.picUrl || defaultPic" class="goods-img" alt="商品图片">
                <div class="goods-detail">
                  <div class="goods-title">{{ order.title || '秒杀商品' }}</div>
                  <div class="goods-sku">规格/SKU: <code>{{ order.skuId }}</code></div>
                </div>
              </div>

              <div class="order-price">
                <span class="price-label">实付款</span>
                <div class="price-amount">￥{{ order.payAmount }}</div>
                <div class="price-tip">免运费</div>
              </div>

              <!-- 操作按钮栏 -->
              <div class="order-action">
                <!-- 未支付订单：显示去支付与取消订单 -->
                <template v-if="order.status === 0 || order.status === 'UNPAID'">
                  <el-button
                    type="danger"
                    size="small"
                    icon="el-icon-wallet"
                    class="pay-now-btn"
                    @click="toPay(order)"
                  >
                    立即支付
                  </el-button>
                  <el-button
                    size="small"
                    type="text"
                    class="cancel-btn"
                    @click="cancelOrder(order.orderId)"
                  >
                    取消订单
                  </el-button>
                </template>

                <!-- 已支付订单 -->
                <template v-else-if="order.status === 1 || order.status === 'PAID'">
                  <el-button size="small" type="success" icon="el-icon-circle-check" plain disabled>
                    已完成扣款
                  </el-button>
                </template>

                <!-- 已取消订单 -->
                <template v-else>
                  <span class="status-closed"><i class="el-icon-circle-close" /> 订单已关闭</span>
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- 3. 分页栏 -->
        <div v-if="total > 0" class="pagination-container">
          <el-pagination
            background
            layout="total, prev, pager, next, jumper"
            :current-page.sync="page"
            :page-size="pageSize"
            :total="total"
            @current-change="fetchOrderList"
          />
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import { getOrderList, cancelOrder } from '@/api/mall'

export default {
  name: 'OrderList',
  data() {
    return {
      userId: 0,
      activeTab: 'ALL',
      loading: false,
      page: 1,
      pageSize: 5,
      total: 0,
      orderList: [],
      defaultPic: 'https://via.placeholder.com/80'
    }
  },
  created() {
    const userInfo = this.$store.state.userInfo
    if (userInfo !== null) {
      this.userId = userInfo.userId
    }
    this.fetchOrderList()
  },
  methods: {
    async fetchOrderList() {
      this.loading = true
      try {
        const params = {
          status: this.activeTab === 'ALL' ? null : this.activeTab,
          pn: this.page
        }
        const resp = await getOrderList(params)
        if (resp.code === 0) {
          this.orderList = resp.data.list
          this.total = resp.data.totalSize
        } else {
          this.$message.error(resp.msg || '获取订单列表失败')
        }
      } catch (err) {
        this.$message.error('网络异常，无法加载订单列表')
      } finally {
        this.loading = false
      }
    },
    handleTabClick() {
      this.page = 1
      this.fetchOrderList()
    },
    toPay(order) {
      this.$router.push({
        path: '/mall/order-pay',
        query: {
          orderId: order.orderId,
          skuId: order.skuId || 1
        }
      })
    },
    cancelOrder(orderId) {
      this.$confirm('确定要取消该订单吗？取消后库存将重新释放。', '取消确认', {
        confirmButtonText: '确定取消',
        cancelButtonText: '暂不取消',
        type: 'warning'
      }).then(async() => {
        try {
          const resp = await cancelOrder(orderId)
          if (resp.code === 0) {
            this.$message.success('订单取消成功')
            this.fetchOrderList()
          } else {
            this.$message.error(resp.msg || '取消订单失败')
          }
        } catch (e) {
          this.$message.error('请求失败，请稍后再试')
        }
      }).catch(() => {})
    },
    getStatusTagType(status) {
      if (status === 0 || status === 'UNPAID') return 'warning'
      if (status === 1 || status === 'PAID') return 'success'
      return 'info'
    },
    getStatusText(status) {
      if (status === 0 || status === 'UNPAID') return '待支付'
      if (status === 1 || status === 'PAID') return '已支付'
      if (status === 2 || status === 'CANCELLED') return '已取消'
      return '未知状态'
    }
  }
}
</script>

<style scoped>
.order-list-container {
  min-height: 100vh;
  background-color: #f8fafc;
  padding: 24px 0;
}

.main-content {
  max-width: 960px;
  margin: 0 auto;
  padding: 0 16px;
}

.main-card {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-icon {
  color: #3b82f6;
}

.refresh-btn {
  color: #64748b;
  font-size: 13px;
}
.refresh-btn:hover {
  color: #3b82f6;
}

.custom-tabs ::v-deep .el-tabs__header {
  margin-bottom: 20px;
}

.order-list-body {
  min-height: 200px;
}

.empty-box {
  padding: 40px 0;
}

.order-item-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 16px;
  background-color: #fff;
  transition: all 0.2s ease;
  overflow: hidden;
}

.order-item-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
}

.order-item-header {
  background-color: #f8fafc;
  padding: 10px 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
  color: #64748b;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.header-left .order-id strong {
  color: #334155;
  font-family: monospace;
}

.status-tag {
  border-radius: 12px;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}
.dot-warning { background-color: #f59e0b; }
.dot-success { background-color: #10b981; }
.dot-info { background-color: #94a3b8; }

.order-item-body {
  display: flex;
  align-items: center;
  padding: 18px;
}

.goods-info {
  flex: 2;
  display: flex;
  align-items: center;
  gap: 14px;
}

.goods-img {
  width: 72px;
  height: 72px;
  border-radius: 6px;
  object-fit: cover;
  border: 1px solid #f1f5f9;
  transition: transform 0.2s ease;
}

.order-item-card:hover .goods-img {
  transform: scale(1.03);
}

.goods-title {
  font-size: 14px;
  color: #1e293b;
  font-weight: 600;
  margin-bottom: 6px;
  line-height: 1.4;
}

.goods-sku {
  font-size: 12px;
  color: #94a3b8;
}

.goods-sku code {
  background: #f1f5f9;
  padding: 1px 4px;
  border-radius: 3px;
  color: #475569;
}

.order-price {
  flex: 1;
  text-align: center;
  border-left: 1px solid #f1f5f9;
  border-right: 1px solid #f1f5f9;
  padding: 0 12px;
}

.price-label {
  font-size: 11px;
  color: #94a3b8;
}

.price-amount {
  font-size: 18px;
  color: #ef4444;
  font-weight: 700;
  margin-top: 2px;
}

.price-tip {
  font-size: 11px;
  color: #10b981;
  margin-top: 2px;
}

.order-action {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.pay-now-btn {
  background-color: #ef4444;
  border-color: #ef4444;
  font-weight: 600;
  padding: 8px 16px;
}

.pay-now-btn:hover {
  background-color: #dc2626;
  border-color: #dc2626;
}

.cancel-btn {
  color: #94a3b8;
  font-size: 12px;
  padding: 0;
}

.cancel-btn:hover {
  color: #ef4444;
}

.status-closed {
  font-size: 13px;
  color: #94a3b8;
}

.pagination-container {
  margin-top: 24px;
  text-align: right;
}
</style>
